import React, { memo, ReactNode } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { motion } from 'framer-motion';

interface WhatsAppStyleNodeProps {
  selected?: boolean;
  icon: ReactNode;
  label: string;
  preview?: ReactNode;
  color: string;
  handles: {
    source?: boolean;
    target?: boolean;
    additionalHandles?: { 
      id: string; 
      position: Position; 
      type: 'source' | 'target'; 
      style?: React.CSSProperties;
      label?: string;
    }[];
  };
  children?: ReactNode;
}

const WhatsAppStyleNode: React.FC<WhatsAppStyleNodeProps> = memo(({
  selected,
  icon,
  label,
  preview,
  color,
  handles,
  children,
}) => {
  // Estrai i colori dal gradient
  const colorMap: Record<string, string> = {
    'from-emerald-400 to-teal-600': '#128C7E',
    'from-blue-500 to-indigo-600': '#34B7F1',
    'from-orange-500 to-amber-600': '#FFA500',
    'from-purple-500 to-pink-600': '#9B59B6',
    'from-indigo-500 to-purple-600': '#6366F1',
    'from-cyan-500 to-sky-600': '#06B6D4',
    'from-slate-600 to-zinc-700': '#64748B',
    'from-orange-500 to-red-600': '#F97316',
    'from-rose-500 to-pink-600': '#F43F5E',
    'from-lime-500 to-green-600': '#84CC16',
    'from-amber-500 to-yellow-600': '#F59E0B',
    'from-violet-500 to-purple-600': '#8B5CF6',
  };

  const bgColor = colorMap[color] || '#128C7E';

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="relative"
    >
      {/* Nodo principale - Stile WhatsApp */}
      <div
        className={`
          relative
          bg-white dark:bg-dark-surface
          rounded-2xl
          px-3 py-2
          shadow-sm hover:shadow-md
          transition-all duration-200
          border
          ${selected 
            ? 'border-whatsapp-green shadow-lg' 
            : 'border-gray-200 dark:border-dark-border'
          }
          min-w-[120px] max-w-[180px]
        `}
      >
        {/* Header con icona e label */}
        <div className="flex items-center gap-2 mb-1">
          <div 
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm"
            style={{ backgroundColor: bgColor }}
          >
            {icon}
          </div>
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200 flex-1 truncate">
            {label}
          </span>
        </div>

        {/* Preview */}
        {preview && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pl-9 -mt-0.5 truncate">
            {preview}
          </div>
        )}

        {/* Children */}
        {children}

        {/* Indicatore di selezione */}
        {selected && (
          <div 
            className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-12 h-1 rounded-full"
            style={{ backgroundColor: bgColor }}
          />
        )}
      </div>

      {/* Handle Target - Più grande e visibile */}
      {handles.target && (
        <Handle
          type="target"
          position={Position.Top}
          className="!w-4 !h-4 !-top-2"
          style={{
            background: '#fff',
            border: `2px solid ${selected ? bgColor : '#ccc'}`,
            borderRadius: '50%',
            cursor: 'crosshair',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.transform = 'scale(1.5)';
            el.style.borderColor = bgColor;
            el.style.background = bgColor;
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.transform = 'scale(1)';
            el.style.borderColor = selected ? bgColor : '#ccc';
            el.style.background = '#fff';
          }}
        />
      )}

      {/* Handle Source - Più grande e visibile */}
      {handles.source && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!w-4 !h-4 !-bottom-2"
          style={{
            background: '#fff',
            border: `2px solid ${selected ? bgColor : '#ccc'}`,
            borderRadius: '50%',
            cursor: 'crosshair',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.transform = 'scale(1.5)';
            el.style.borderColor = bgColor;
            el.style.background = bgColor;
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.transform = 'scale(1)';
            el.style.borderColor = selected ? bgColor : '#ccc';
            el.style.background = '#fff';
          }}
        />
      )}

      {/* Additional Handles per condizioni */}
      {handles.additionalHandles?.map((handle) => {
        const isTrue = handle.label?.includes('Vero');
        const handleColor = isTrue ? '#10B981' : '#EF4444';
        
        return (
          <div key={handle.id} className="absolute" style={handle.style}>
            {handle.label && (
              <span 
                className="absolute -right-12 top-1/2 -translate-y-1/2 text-[10px] font-medium whitespace-nowrap"
                style={{ color: handleColor }}
              >
                {handle.label}
              </span>
            )}
            <Handle
              id={handle.id}
              type={handle.type}
              position={handle.position}
              className="!w-4 !h-4"
              style={{
                background: '#fff',
                border: `2px solid ${handleColor}`,
                borderRadius: '50%',
                cursor: 'crosshair',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.transform = 'scale(1.5)';
                el.style.background = handleColor;
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.transform = 'scale(1)';
                el.style.background = '#fff';
              }}
            />
          </div>
        );
      })}
    </motion.div>
  );
});

WhatsAppStyleNode.displayName = 'WhatsAppStyleNode';

export default WhatsAppStyleNode;
